import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import {
  CharterActRefDto,
  CompanyCorporateCharterDto,
} from '@interfaces/corporate-charter/responses/corporate-charter.response';
import { VaultDatePipe } from '@shared/pipes/vault-date.pipe';
import {
  ADMINISTRATION_MODEL_LABEL,
  LEGAL_NATURE_LABEL,
  formatMoney,
  isCorporation,
} from '@features/dashboard/company/corporate-charter/corporate-charter.util';

/**
 * Faixa de resumo do Societário (mesmo padrão da faixa de KPIs do Data Room): natureza, capital, sócios, modelo de
 * administração, ato vigente com a data e a conferência. "N atos para revisar" é uma ação: leva à linha do tempo.
 */
@Component({
  selector: 'app-charter-summary-strip',
  imports: [VaultDatePipe],
  templateUrl: './charter-summary-strip.component.html',
  styleUrl: './charter-summary-strip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharterSummaryStripComponent {
  readonly charter = input.required<CompanyCorporateCharterDto>();
  /** Ato mais recente aplicado (o documento vigente). */
  readonly currentAct = input<CharterActRefDto | null>(null);
  readonly processingCount = input(0);

  /** "Ver atos para revisar": a página troca para a linha do tempo. */
  readonly reviewRequested = output<void>();

  protected readonly nature = computed(() => {
    const value = this.charter().legalNature;
    return value ? LEGAL_NATURE_LABEL[value] : null;
  });

  protected readonly capital = computed(() => {
    const c = this.charter().capital;
    return c.shareCapital.value == null ? null : formatMoney(c.shareCapital.value, c.currency);
  });

  protected readonly holders = computed(() => {
    const c = this.charter();
    const corp = isCorporation(c.legalNature) || c.capital.unitKind === 'shares';
    const noun = corp ? 'acionista' : 'sócio';
    if (c.holdersStatus === 'not-disclosed' || c.holders.length === 0) {
      return { count: null, text: `${noun}s não informados` };
    }
    const count = c.holders.length;
    const partial = c.holdersStatus === 'partial' ? ' (lista parcial)' : '';
    return { count, text: `${noun}${count === 1 ? '' : 's'}${partial}` };
  });

  protected readonly administration = computed(() => {
    const value = this.charter().governance.administrationModel.value;
    return value ? ADMINISTRATION_MODEL_LABEL[value] : null;
  });

  protected readonly actDate = computed(() => {
    const act = this.currentAct();
    return act?.registeredAt ?? act?.signedAt ?? null;
  });

  protected readonly baseLabel = computed(() => {
    const base = this.charter().baseAct;
    const current = this.currentAct();
    return base && current && base.actId !== current.actId ? base.label : null;
  });

  protected readonly review = computed(() => {
    const c = this.charter();
    if (c.status === 'needs-review' && c.pendingReviewCount > 0) {
      const n = c.pendingReviewCount;
      return n === 1 ? '1 ato para revisar' : `${n} atos para revisar`;
    }
    return null;
  });

  protected readonly missing = computed(() => this.charter().missingActs ?? []);
}
